'use client';

import React, { useRef, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons/faArrowUpRightFromSquare';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { IconButton } from '@osmosis/tailwind-ui/components/IconButton';
import { H2, P } from '@osmosis/tailwind-ui/components/Text';
import { bg0 } from '@osmosis/tailwind-ui/styles/background';
import { borderColor300 } from '@osmosis/tailwind-ui/styles/border';
import { text5 } from '@osmosis/tailwind-ui/styles/text';
import { useAIPanelContext } from '../../context/AIPanelContext';
import LoggedOutAIPanelContent from './LoggedOutAIPanelContent';
import { OAIIframeBody } from './OAIIframeBody';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { usePagesContext } from '@osmosis/nextjs-components/context/PagesProvider';
import { track } from '../../lib/util/tracking';
import { OAIMode } from '../../lib/util/oai';
import { Tooltip } from '@osmosis/tailwind-ui/components/tooltip/Tooltip';
import { useColors } from '@osmosis/tailwind-ui/helpers/useColors';
import { ZIndex } from '@osmosis/tailwind-ui/helpers/zIndex';
import { ExamplePrompts } from './ExamplePrompts';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons/faSparkles';
import type { TrialCtaArgs } from '../../lib/composer/schemas/answersValueProp';

type Props = {
  /**
   * When true the panel stays mounted in a persistent rail: instead of hiding
   * while closed it renders a compact collapsed header bar that expands the
   * panel on click. Used by the persistent right-rail layout (`keepSidebarOpen`).
   */
  collapsible?: boolean;
  /**
   * Overrides the "Hide" button behavior. Defaults to collapsing the panel
   * (`closePanel`). The persistent-rail layout passes a handler that also
   * closes the mobile section overlay.
   */
  onHide?: () => void;
  /**
   * Makes the panel fill its container width instead of the resizable
   * `panelWidth`. The viewport-based `max-md:` full-width override only triggers
   * below 768px; the mobile section overlay can render at wider viewports (its
   * mobile/desktop switch is container-width based), so it passes this to force
   * full width regardless of viewport.
   */
  fitContainer?: boolean;
  /** Answer-page-only shared signup CTA copy for the logged-out promo. */
  trialCta?: TrialCtaArgs;
};

type HeadingProps = {
  title: string;
  'data-testid'?: string;
};

const COMMON_BUTTON_CLASS = `${text5} !px-2 text-sm font-bold hover:bg-blue-100 hover:text-darkest-blue-500`;

const Heading = ({ title, 'data-testid': dataTestId }: HeadingProps) => {
  return (
    <H2
      data-testid={dataTestId}
      // Permanently focusable so the skip link can land focus here and keep it
      // (removing a transient tabIndex would blur a non-native focus target).
      tabIndex={-1}
      className={cn(
        '!text-lg !font-bold flex flex-nowrap justify-center items-center gap-1',
        'rounded px-2 py-0.5 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-700',
        text5,
      )}
    >
      <Icon icon={faSparkles} size={15} />
      {title}
    </H2>
  );
};

/**
 * Resizable right-side AI chat panel.
 * Panel content (iframe) is a placeholder — to be implemented in a future iteration.
 */
const AIPanel = ({
  collapsible = false,
  onHide,
  fitContainer = false,
  trialCta,
}: Props) => {
  const { formatMessage } = useIntl();
  const {
    isOpen,
    isLoggedOut,
    panelWidth,
    isDragging,
    openPanel,
    closePanel,
    iframeRef,
    iframeSrc,
    iframeLoading,
    iframeError,
    retryLoadIframe,
    recordInteraction,
    openInNewTab,
    topic,
    showExamplePrompts,
    promptsExamplesCollapsed,
    promptSubmitted,
    shouldRenderAIChat,
    togglePromptsExamples,
    sendOAIUserMessage,
    updateOAIPageContext,
    focusPanelNonce,
  } = useAIPanelContext();
  const { pathname } = usePagesContext();

  const handlePromptSelected = (prompt: string, mode: OAIMode) => {
    updateOAIPageContext({
      useInitialPageContext: true,
    });
    sendOAIUserMessage(prompt, mode);
  };

  const blurDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const recordIframeInteraction = () => {
      recordInteraction();
      track('osmosis-ai-embed-clicked', { page: pathname }, ['keen', 'pendo']);
    };
    const checkIframeFocus = () => {
      if (document.activeElement !== iframeRef.current) return;
      if (blurDebounceRef.current) clearTimeout(blurDebounceRef.current);
      blurDebounceRef.current = setTimeout(recordIframeInteraction, 500);
    };
    const handleBlur = () => {
      // Use a microtask so activeElement has updated before we check it.
      // When an iframe captures focus (mouse click or touch tap), it becomes
      // document.activeElement in the parent document — works on all platforms.
      setTimeout(checkIframeFocus, 0);
    };
    window.addEventListener('blur', handleBlur);
    return () => {
      window.removeEventListener('blur', handleBlur);
      if (blurDebounceRef.current) clearTimeout(blurDebounceRef.current);
    };
  }, [recordInteraction, pathname]);

  const panelRef = useRef<HTMLElement>(null);

  const colors = useColors();

  const collapseButtonTitle = formatMessage({
    id: 'ai-panel.close',
    defaultMessage: 'Close',
  });

  const expandButtonTitle = formatMessage({
    id: 'ai-panel.open',
    defaultMessage: 'Open Osmosis AI panel',
  });

  const panelTitle = formatMessage({
    id: 'ai-panel.title',
    defaultMessage: 'Ask Osmosis AI',
  });

  const openNewTabTitle = formatMessage({
    id: 'ai-panel.open-new-tab',
    defaultMessage: 'Open in new tab',
  });

  const newTabLabel = formatMessage({
    id: 'ai-panel.new-tab',
    defaultMessage: 'New Tab',
  });

  const hideLabel = formatMessage({
    id: 'ai-panel.hide',
    defaultMessage: 'Hide',
  });

  const onOpenInNewTab = () => {
    track('osmosis-ai-embed-open-new-tab-clicked', { page: pathname }, [
      'keen',
      'pendo',
    ]);
    openInNewTab({ withQuery: true, shouldInitFreshChat: false });
  };

  const onRetry = () => {
    track('osmosis-ai-embed-retry-clicked', { page: pathname }, [
      'keen',
      'pendo',
    ]);
    retryLoadIframe();
  };

  // Move focus into the panel only when the user opens it (a closed -> open
  // transition), not on initial mount when it's already open on page load.
  // Stealing focus on mount would bypass the global skip links, which must be
  // the first thing a keyboard user reaches (they jump *into* the panel via
  // the dedicated OAI skip link).
  // Focus the panel only on a user-initiated open (focusPanelNonce bumps), not
  // on the initial open-state restore. The initial render has nonce 0, which we
  // skip, so an answer/learn page that loads with the panel already open leaves
  // focus at the top of the document where the global skip links live.
  const didMountFocusRef = useRef(false);
  useEffect(() => {
    if (!didMountFocusRef.current) {
      didMountFocusRef.current = true;
      return;
    }
    panelRef.current?.focus();
  }, [focusPanelNonce]);

  const onOpenExpand = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    openPanel();
  };

  if (!shouldRenderAIChat) return null;

  return (
    <>
      {collapsible && !isOpen && (
        // eslint-disable-next-line @nx/workspace/a11y/require-keyboard-handlers
        <Flex
          className={cn(
            'w-full items-center justify-between rounded-lg border px-4 py-1.5 cursor-pointer min-h-[46px]',
            borderColor300,
            bg0,
          )}
          data-testid="ai-panel-collapsed"
          onClick={onOpenExpand}
        >
          <Heading title={panelTitle} data-testid="ai-panel-heading" />
          <IconButton
            variant="transparent"
            icon={<Icon icon={faChevronDown} size={14} className={text5} />}
            data-testid="ai-panel-expand-btn"
            ariaLabel={expandButtonTitle}
            onClick={onOpenExpand}
            data-tooltip-id="panel-open-tooltip"
            className={cn(COMMON_BUTTON_CLASS, 'text-2xl h-8 w-8')}
          />
          <Tooltip
            id="panel-open-tooltip"
            style={{
              backgroundColor: colors.text,
              color: colors.textInvert,
              zIndex: ZIndex.MODAL,
            }}
          >
            <P color="inherit" className={`text-[13px]`}>
              {expandButtonTitle}
            </P>
          </Tooltip>
        </Flex>
      )}
      <aside
        id="ai-panel"
        ref={panelRef}
        // target for the skip link
        tabIndex={-1}
        aria-label={formatMessage({
          id: 'ai-panel.region',
          defaultMessage: 'Osmosis AI assistant',
        })}
        className={cn(
          'h-full flex-col overflow-hidden p-0 outline-none max-md:!w-full max-md:!max-w-full rounded-lg border ',
          // Visible focus indicator when the skip link lands focus here
          'focus:ring-2 focus:ring-inset focus:ring-blue-700',
          bg0,
          fitContainer && '!w-full !min-w-0 !max-w-full',
          isOpen ? 'flex' : 'hidden',
          borderColor300,
        )}
        style={{
          width: fitContainer ? '100%' : panelWidth,
          minWidth: fitContainer ? 0 : 280,
          flexShrink: 0,
          transition: 'width ease',
        }}
        data-testid="ai-panel"
      >
        {/* Panel Header */}
        <Flex
          className={`items-center justify-between border-b px-4 py-1 min-h-[46px] ${borderColor300}`}
        >
          <Heading title={panelTitle} data-testid="ai-panel-heading" />
          <Flex
            className="items-center gap-2"
            role="toolbar"
            aria-label={formatMessage({
              id: 'ai-panel.actions',
              defaultMessage: 'Panel actions',
            })}
          >
            {/* Open in new tab - visible when user is logged in */}
            {isLoggedOut ? null : (
              <Button
                variant="transparent"
                color="transparent"
                startIcon={<Icon icon={faArrowUpRightFromSquare} size={14} />}
                data-testid="ai-panel-open-new-tab-btn"
                aria-label={openNewTabTitle}
                className={cn(COMMON_BUTTON_CLASS)}
                onClick={onOpenInNewTab}
              >
                {newTabLabel}
              </Button>
            )}
            {/* Hide (close) button */}
            <Button
              variant="transparent"
              color="transparent"
              startIcon={<Icon icon={faXmark} size={15} />}
              data-testid="ai-panel-close-btn"
              aria-label={collapseButtonTitle}
              className={cn(COMMON_BUTTON_CLASS)}
              onClick={onHide ?? closePanel}
            >
              {hideLabel}
            </Button>
          </Flex>
        </Flex>

        {/* Example prompts — visible when AI is available and topic is known */}
        {!isLoggedOut && (
          <ExamplePrompts
            topic={topic}
            onPromptSelected={handlePromptSelected}
            collapsed={promptsExamplesCollapsed}
            togglePromptsSection={togglePromptsExamples}
            showExamplePrompts={showExamplePrompts}
            promptSubmitted={promptSubmitted}
          />
        )}

        {/* Drag overlay — blocks pointer events to iframe while PanelResizer is being dragged */}
        {isDragging && (
          <Box
            className="absolute inset-0 z-10 cursor-ew-resize"
            data-testid="ai-panel-drag-overlay"
            aria-hidden="true"
          />
        )}

        {/* Panel Body — static promo for logged-out users, iframe chat otherwise */}
        {isLoggedOut ? (
          <LoggedOutAIPanelContent trialCta={trialCta} />
        ) : (
          <OAIIframeBody
            iframeRef={iframeRef}
            iframeSrc={iframeSrc}
            iframeLoading={iframeLoading}
            iframeError={iframeError}
            onRetry={onRetry}
          />
        )}
      </aside>
    </>
  );
};

export default AIPanel;
